import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { Alert, Avatar, Button, Card, StatusBadge } from '@rallyshift/design-system'
import api from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import { useOrganization } from '../contexts/OrganizationContext'
import { statusLabel } from '../utils/status'
import { formatTimeRangeToLocal } from '../utils/timezone'

interface Assignment {
  id: string
  status: string
  attendance?: string
  shift?: {
    id: string
    title: string
    start_time: string
    end_time: string
    location: string
  }
}

const DAY_NAMES = [
  'Zondag',
  'Maandag',
  'Dinsdag',
  'Woensdag',
  'Donderdag',
  'Vrijdag',
  'Zaterdag',
]

/**
 * Volunteer check-in for the event itself (design screen 1c). The only surface in the
 * product that uses the `.rs-night` dark scope, because this one is genuinely used
 * outdoors after dark, one-handed, by someone who is already cold.
 */
const CheckInPage: React.FC = () => {
  const { user } = useAuth()
  const { organization } = useOrganization()

  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [team, setTeam] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [])

  const load = useCallback(async () => {
    try {
      setAssignments(await api.shifts.getMyAssignments())
    } catch (e: any) {
      setError(e.message || 'Je diensten konden niet worden geladen.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  /** The shift that is running now, or else the next one starting within six hours. */
  const current = useMemo(() => {
    const live = assignments
      .filter((a) => a.shift && a.status !== 'cancelled')
      .map((a) => ({
        a,
        start: new Date(a.shift!.start_time).getTime(),
        end: new Date(a.shift!.end_time).getTime(),
      }))
      .sort((x, y) => x.start - y.start)

    return (
      live.find((s) => now >= s.start && now <= s.end) ??
      live.find((s) => s.start > now && s.start - now < 6 * 60 * 60 * 1000) ??
      null
    )
  }, [assignments, now])

  useEffect(() => {
    if (!current?.a.shift) return
    api.shifts
      .getAssignments(current.a.shift.id)
      .then((rows) => setTeam((rows || []).filter((r: any) => r.volunteer_id !== user?.id)))
      .catch(() => setTeam([]))
  }, [current, user?.id])

  const checkIn = async () => {
    if (!current) return
    setBusy(true)
    try {
      const updated = await api.shifts.setAttendance(current.a.id, 'checked-in')
      setAssignments((prev) =>
        prev.map((a) => (a.id === current.a.id ? { ...a, attendance: updated.attendance } : a)),
      )
    } catch (e: any) {
      setError(e.message || 'Inchecken is niet gelukt.')
    } finally {
      setBusy(false)
    }
  }

  // A bookmarked or shared /check-in link should land somewhere useful, not on a
  // button the API will refuse.
  if (organization?.volunteer_check_in_enabled === false) {
    return <Navigate to="/my-shifts" replace />
  }

  const body = () => {
    if (loading) {
      return (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-tenant-brand border-r-transparent" />
        </div>
      )
    }

    if (!current?.a.shift) {
      return (
        <div className="px-6 py-16 text-center">
          <h1 className="font-display text-display-md text-text-primary">
            Je hebt nu geen dienst
          </h1>
          <p className="mt-2 text-md text-text-secondary">
            Zodra je dienst begint, kun je hier inchecken.
          </p>
        </div>
      )
    }

    const shift = current.a.shift
    const start = current.start
    const minutesToStart = Math.round((start - now) / 60_000)
    const checkedIn = current.a.attendance === 'checked-in' || current.a.attendance === 'checked-out'

    return (
      <div className="mx-auto w-full max-w-md px-5 py-8">
        <div className="text-center">
          <p className="text-md text-text-secondary">
            {DAY_NAMES[new Date(shift.start_time).getDay()]} ·{' '}
            {formatTimeRangeToLocal(shift.start_time, shift.end_time)}
          </p>
          <h1 className="mt-2 font-display text-[30px] leading-tight text-text-primary">
            {shift.title}
          </h1>
          <p className="mt-2 text-md text-text-secondary">{shift.location}</p>
        </div>

        {error && (
          <div className="mt-6">
            <Alert variant="danger" onDismiss={() => setError('')}>
              {error}
            </Alert>
          </div>
        )}

        <div className="mt-8">
          {checkedIn ? (
            <div className="flex min-h-[76px] items-center justify-center rounded-lg bg-status-success-bg text-lg font-semibold text-status-success-text">
              {'\u25C9'} Je bent ingecheckt
            </div>
          ) : (
            <Button
              size="lg"
              fullWidth
              loading={busy}
              onClick={checkIn}
              className="min-h-[76px] text-[20px]"
            >
              {'\u25C9'} Check in
            </Button>
          )}
        </div>

        {!checkedIn && minutesToStart > 0 && (
          <p className="mt-4 text-center text-md text-text-secondary">
            Je begint over <strong className="text-text-primary">{minutesToStart} minuten</strong>
          </p>
        )}

        {team.length > 0 && (
          <div className="mt-8">
            <Card title="Je team vandaag">
              <ul className="space-y-3">
                {team.map((member) => (
                  <li key={member.id} className="flex items-center gap-3">
                    <Avatar
                      name={`${member.first_name ?? ''} ${member.last_name ?? ''}`.trim() || member.email}
                      size={28}
                    />
                    <span className="min-w-0 flex-1 truncate text-md text-text-primary">
                      {member.first_name || member.email}
                    </span>
                    <StatusBadge
                      dimension="attendance"
                      status={member.attendance || 'expected'}
                      size="sm"
                      label={statusLabel('attendance', member.attendance || 'expected')}
                    />
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}

        <p className="mt-8 text-center">
          <Link to="/my-shifts" className="text-md text-text-secondary underline">
            Ik red het niet — meld het even
          </Link>
        </p>
      </div>
    )
  }

  return (
    <div className="rs-night min-h-screen bg-surface-page">
      <header className="flex items-center gap-3 bg-surface-sunken px-5 py-4">
        {organization?.logo_url ? (
          <img src={organization.logo_url} alt={organization.name} className="h-8" />
        ) : (
          <span className="font-display text-lg text-text-primary">{organization?.name}</span>
        )}
        <span className="ml-auto text-sm text-text-secondary">Vandaag</span>
      </header>
      {body()}
    </div>
  )
}

export default CheckInPage
